import type { ReactNode } from "react";
import { useSyncExternalStore } from "react";
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner";

import { Banner, type BannerVariant } from "~/components/ui-kit";
import { getResolvedTheme, subscribeTheme } from "~/lib/theme";

/**
 * BLOC Design System (BDS) トースト。
 *
 * エンジンは sonner（キュー / タイマー / hover で一時停止 / スワイプ解除 /
 * aria-live を内蔵）。見た目は BDS の {@link Banner} を `toast.custom` で描画し、
 * sonner の既定スタイルは `unstyled` で無効化する（＝色・角丸・枠は global.css の
 * トークン経由で Banner が一手に持つ）。これで「sonner の堅牢な挙動 + BDS の見た目」
 * を両立する。
 *
 * 使い方:
 *   import { toast } from "~/components/toast";
 *   toast.success("保存しました");
 *   toast.error("失敗しました", { description: e.message });
 *   toast.info("公開予約を設定しました", {
 *     action: { label: "取り消す", onClick: () => cancel() },
 *   });
 *
 * `<BlocToaster />` は main.tsx に 1 回だけマウントする（チャンク非依存）。
 */

const TOAST_WIDTH = "w-[clamp(300px,calc(100vw-2rem),384px)]";

/**
 * 表示時間の標準（2026-07-18 調査反映）:
 * - IBM Carbon: 自動消去は 5 秒推奨・アクション付きは自動で消さない
 *   (carbondesignsystem.com/components/notification/usage)
 * - a11y 定説 (Byrne-Haber): 5 秒 + 120 語ごとに 1 秒 — 実質最短 6 秒
 * - Friedman: 表示 2〜7 秒・読み速度ベースで 50ms×文字数（英語）
 * - W3C std-toast 議論: error とアクション付きは永続（手動 dismiss）が方向性
 *
 * BLOC の適用（日本語 UI — 読字 〜500 字/分 ≒ 100ms/字）:
 *   duration = clamp(2000 + 100ms × 文字数, 下限, 上限)
 *   info/success: 下限 5000 / 上限 10000。warn: 下限 7000 / 上限 12000。
 *   error とアクション付き: 永続（0）— sonner の hover 一時停止・Banner の
 *   クローズボタンが前提。opts.duration 指定は常に優先（0 = 永続）。
 */
const DURATION_BOUNDS: Record<BannerVariant, { min: number; max: number }> = {
  info: { min: 5000, max: 10000 },
  success: { min: 5000, max: 10000 },
  warn: { min: 7000, max: 12000 },
  error: { min: 0, max: 0 }, // 使われない（error は常に永続）
};

const MS_PER_CHAR = 100;
const BASE_MS = 2000;

/** ReactNode からおおよその可読文字数を数える（string/number のみ集計 — 要素は無視）。 */
export function countReadableChars(node: ReactNode): number {
  if (node === null || node === undefined || typeof node === "boolean") return 0;
  if (typeof node === "string") return node.length;
  if (typeof node === "number") return String(node).length;
  if (Array.isArray(node)) return node.reduce<number>((n, c) => n + countReadableChars(c), 0);
  return 0;
}

/**
 * variant と本文長から表示時間(ms)を決める純関数。error とアクション付きは
 * 0（永続）。それ以外は読み速度ベースの下限/上限クランプ。
 */
export function computeToastDuration(
  variant: BannerVariant,
  chars: number,
  hasAction: boolean,
): number {
  if (variant === "error" || hasAction) return 0;
  const { min, max } = DURATION_BOUNDS[variant];
  return Math.min(max, Math.max(min, BASE_MS + chars * MS_PER_CHAR));
}

export type ToastOptions = {
  /** タイトル下の補足。エラーメッセージや詳細に。 */
  description?: ReactNode;
  /** 表示時間(ms)。0 で永続（手動 dismiss 必須）。未指定は variant 既定。 */
  duration?: number;
  /** 右側のアクションボタン（取り消し・再試行など）。 */
  action?: { label: string; onClick: () => void };
  /** 同一 id で呼ぶと既存トーストを更新（連打の重複防止に）。 */
  id?: string | number;
  /** 時間切れ・閉じる・アクション後など、トーストが消えたとき。 */
  onClose?: () => void;
};

function show(
  variant: BannerVariant,
  title: ReactNode,
  opts?: ToastOptions,
): string | number {
  return sonnerToast.custom(
    (id) => (
      <div className={TOAST_WIDTH}>
        <Banner
          variant={variant}
          title={title}
          onClose={() => sonnerToast.dismiss(id)}
          className="shadow-lg"
          actions={
            opts?.action ? (
              <button
                type="button"
                onClick={() => {
                  opts.action!.onClick();
                  sonnerToast.dismiss(id);
                }}
                className="rounded-md px-2 py-0.5 text-[12px] font-semibold underline-offset-2 hover:underline"
              >
                {opts.action.label}
              </button>
            ) : undefined
          }
        >
          {opts?.description}
        </Banner>
      </div>
    ),
    {
      duration: resolveSonnerDuration(
        opts?.duration ??
          computeToastDuration(
            variant,
            countReadableChars(title) + countReadableChars(opts?.description),
            Boolean(opts?.action),
          ),
      ),
      id:
        opts?.id ??
        (variant === "error"
          ? deriveErrorDedupeId(title, opts?.description)
          : undefined),
      onAutoClose: opts?.onClose,
      onDismiss: opts?.onClose,
    },
  );
}

/** 0（= 永続）を sonner の流儀（Infinity）に変換する。sonner は 0 を falsy として
 * 既定 4000ms に落とすため、0 のまま渡すと永続にならない（sonner 2.0.7
 * `toast.duration || … || 4000` — codex レビューで実装裏取り済み）。 */
export function resolveSonnerDuration(ms: number): number {
  return ms === 0 ? Number.POSITIVE_INFINITY : ms;
}

/**
 * error は永続化したため、同一メッセージの連発（例: 参照ピッカーの上限超過を
 * クリックのたびに通知）で DOM に無限に積もるのを防ぐ — id 未指定の error は
 * 本文から安定 id を導出し、同一内容は既存トーストの置換（sonner の同 id
 * 更新）にする。異なる内容の error は従来どおり別トースト。
 */
export function deriveErrorDedupeId(
  title: ReactNode,
  description: ReactNode,
): string | undefined {
  const text =
    typeof title === "string" || typeof title === "number"
      ? String(title)
      : undefined;
  if (text === undefined) return undefined; // JSX タイトルは dedupe しない（安定キーが無い）
  const desc =
    typeof description === "string" || typeof description === "number"
      ? String(description)
      : description == null
        ? ""
        : "[jsx]"; // JSX description は空文字と区別（誤 dedupe 防止）
  // 長さプレフィクスで区切り文字の衝突を排除（"A:B"+"C" と "A"+"B:C" が同 id に
  // ならないように — codex レビュー指摘）。
  return `bloc-error:${text.length}:${text}|${desc.length}:${desc}`;
}

export const toast = {
  info: (title: ReactNode, opts?: ToastOptions) => show("info", title, opts),
  success: (title: ReactNode, opts?: ToastOptions) => show("success", title, opts),
  warn: (title: ReactNode, opts?: ToastOptions) => show("warn", title, opts),
  error: (title: ReactNode, opts?: ToastOptions) => show("error", title, opts),
  /** id 指定（または全件）でトーストを閉じる。 */
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
};

/**
 * トーストの描画コンテナ。BDS 既定: 右下・縦積み・hover で展開。
 * `unstyled` で sonner 既定スキンを外し、各トーストの Banner に見た目を委ねる。
 * theme は sonner の "system"（= OS 直読み）ではなくアプリの実効テーマを渡す —
 * 「ライト/ダーク」強制時に OS 側と食い違うと、トーストだけ逆テーマになるため。
 */
export function BlocToaster() {
  const resolved = useSyncExternalStore(subscribeTheme, getResolvedTheme, () => "light" as const);
  return (
    <SonnerToaster
      containerAriaLabel="通知"
      position="bottom-right"
      theme={resolved}
      gap={10}
      offset={20}
      visibleToasts={4}
      expand
      toastOptions={{ unstyled: true }}
    />
  );
}
